import {useState} from 'react';
import {
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Page,
  Select,
  Text,
} from '@shopify/polaris';
import type {PagePropsBase} from '@slj/ui/document';
import {AppFrame} from '@slj/ui/app-frame';
import {EmptyState} from '@slj/ui/patterns';

import {AppAccessForm, type AppToggle} from './access-form';
import type {ChromeProps} from './dashboard';
import {identityAccount, identitySections} from './nav';

export interface GroupDetailProps extends PagePropsBase {
  chrome: ChromeProps;
  group: {id: string; name: string};
  apps: AppToggle[];
  members: {userId: string; name: string; email: string}[];
  /** Users not already in the group — the add-member choices. */
  candidates: {id: string; label: string}[];
  ok?: string;
  error?: string;
}

export function GroupDetailPage({chrome, group, apps, members, candidates, ok, error}: GroupDetailProps) {
  const {user} = chrome;
  const [addUser, setAddUser] = useState(candidates[0]?.id ?? '');

  return (
    <AppFrame
      logoUrl="/assets/logo.svg"
      user={user}
      sections={identitySections(user.isAdmin)}
      currentPath={chrome.path}
      account={identityAccount}
    >
      <Page
        title={group.name}
        backAction={{content: 'Groups', url: '/admin/groups'}}
        subtitle="Everyone in this group gets the apps granted to it."
      >
        <BlockStack gap="500">
          {ok ? <Banner tone="success"><p>{ok}</p></Banner> : null}
          {error ? <Banner tone="critical"><p>{error}</p></Banner> : null}

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                App access
              </Text>
              <Text as="p" tone="subdued">
                Apps ticked here are granted to every member of this group.
              </Text>
              <AppAccessForm action={`/admin/groups/${group.id}/apps`} apps={apps} />
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Members ({members.length})
              </Text>

              <form method="post" action={`/admin/groups/${group.id}/members/add`}>
                <InlineStack gap="300" wrap={false} blockAlign="end">
                  <div style={{flexGrow: 1}}>
                    <Select
                      label="Add a member"
                      name="userId"
                      options={
                        candidates.length
                          ? candidates.map((c) => ({label: c.label, value: c.id}))
                          : [{label: 'Everyone is already a member', value: ''}]
                      }
                      value={addUser}
                      onChange={setAddUser}
                      disabled={candidates.length === 0}
                    />
                  </div>
                  <Button submit variant="primary" disabled={candidates.length === 0}>
                    Add
                  </Button>
                </InlineStack>
              </form>

              {members.length === 0 ? (
                <EmptyState density="compact">No members yet.</EmptyState>
              ) : (
                <BlockStack gap="200">
                  {members.map((m) => (
                    <InlineStack key={m.userId} align="space-between" blockAlign="center" wrap={false}>
                      <BlockStack gap="0">
                        <Text as="span" fontWeight="medium">
                          {m.name}
                        </Text>
                        <Text as="span" tone="subdued" variant="bodySm">
                          {m.email}
                        </Text>
                      </BlockStack>
                      <form method="post" action={`/admin/groups/${group.id}/members/remove`}>
                        <input type="hidden" name="userId" value={m.userId} />
                        <Button submit size="slim">
                          Remove
                        </Button>
                      </form>
                    </InlineStack>
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </BlockStack>
      </Page>
    </AppFrame>
  );
}
